import type { Filters } from '@/lib/filters'

import type {
    Agreement,
    BudgetRow,
    ComplianceItem,
    Criticality,
    Dataset,
    DesignActivity,
    Dims,
    Finding,
    HistoryRow,
    Initiative,
    InitiativeStatus,
    Period,
    Rag,
    SheetKey
} from '@/lib/data/types'

import { DAY, clamp, normalizeValue, quarterOf } from '@/lib/data/normalize'
import { periodRange, quarterKey, yearKey } from '@/lib/filters'

/* ------------------------------------------------------------------ */
/* Thresholds                                                          */
/* ------------------------------------------------------------------ */

export interface Thresholds {
    /** Gap in percentage points (plan minus actual) that turns amber */
    amber: number
    /** Gap in percentage points that turns red */
    red: number
    /** Days ahead that count as "due soon" */
    dueSoon: number
}

/** RAG thresholds come from the parameters sheet when present. */
export const thresholdsOf = (ds: Dataset): Thresholds => {
    const amber = ds.params.amberPp !== null && ds.params.amberPp >= 0 ? ds.params.amberPp : 5
    const red = ds.params.redPp !== null && ds.params.redPp > amber ? ds.params.redPp : Math.max(15, amber + 1)
    const dueSoon = ds.params.dueSoonDays !== null && ds.params.dueSoonDays > 0 ? Math.round(ds.params.dueSoonDays) : 14

    return { amber, red, dueSoon }
}

export const utcDay = (ts: number): number => {
    const date = new Date(ts)

    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

export const daysBetween = (from: number, to: number): number => Math.round((to - from) / DAY)

/* ------------------------------------------------------------------ */
/* Cut-off dates                                                       */
/* ------------------------------------------------------------------ */

/** Reference dates available in the file, ascending. */
export const cutOptions = (ds: Dataset): number[] => {
    const set = new Set<number>()

    for (const row of ds.history) set.add(row.cut)
    if (ds.params.cutDate !== null) set.add(ds.params.cutDate)
    if (!set.size) set.add(utcDay(ds.quality.loadedAt))

    return [...set].sort((a, b) => a - b)
}

/* ------------------------------------------------------------------ */
/* Portfolio snapshots                                                 */
/* ------------------------------------------------------------------ */

export interface SnapItem extends Dims {
    key: string
    id: string
    name: string
    tower: string
    status: InitiativeStatus
    progress: number | null
    planned: number | null
    start: number | null
    end: number | null
    overdue: boolean
    daysLate: number | null
    gap: number | null
    health: Rag
    budget: number | null
    notes: string
}

/** Linear expected progress between start and end at a given date. */
export const linearPlan = (start: number | null, end: number | null, at: number): number | null => {
    if (start === null || end === null) return null
    if (end <= start) return at >= end ? 100 : 0

    return clamp(((at - start) / (end - start)) * 100, 0, 100)
}

const OPEN: InitiativeStatus[] = ['active', 'onHold', 'backlog']

const healthOf = (item: Omit<SnapItem, 'health'>, explicit: Rag | null, th: Thresholds): Rag => {
    if (item.status === 'closed' || item.status === 'cancelled') return 'none'
    if (explicit) return explicit
    if (item.status === 'backlog') return item.overdue ? 'red' : 'none'
    if (item.overdue) return 'red'
    if (item.gap !== null && item.gap > th.red) return 'red'
    if (item.gap !== null && item.gap > th.amber) return 'amber'
    if (item.status === 'onHold') return 'amber'

    return 'green'
}

const makeItem = (
    base: Initiative | HistoryRow,
    values: { progress: number | null, planned: number | null, status: InitiativeStatus, end: number | null, start: number | null },
    at: number,
    explicitHealth: Rag | null,
    th: Thresholds
): SnapItem => {
    const planned = values.planned ?? linearPlan(values.start, values.end, at)
    const progress = values.progress ?? (values.status === 'closed' ? 100 : null)
    const overdue = values.end !== null && values.end < at && OPEN.includes(values.status)
    const gap = planned !== null && progress !== null && values.status !== 'closed' ? planned - progress : null

    const item: Omit<SnapItem, 'health'> = {
        key: base.key,
        id: base.id,
        name: base.name,
        division: base.division,
        bu: base.bu,
        owner: base.owner,
        program: base.program,
        domain: base.domain,
        criticality: base.criticality,
        tower: 'tower' in base ? base.tower : '',
        status: values.status,
        progress,
        planned,
        start: values.start,
        end: values.end,
        overdue,
        daysLate: overdue && values.end !== null ? daysBetween(values.end, at) : null,
        gap,
        budget: 'budget' in base ? base.budget : null,
        notes: 'notes' in base ? base.notes : ''
    }

    return { ...item, health: healthOf(item, explicitHealth, th) }
}

const statusFromProgress = (progress: number | null): InitiativeStatus => {
    if (progress === null || progress <= 0) return 'backlog'
    if (progress >= 100) return 'closed'

    return 'active'
}

/**
 * Portfolio as of every cut-off date. The latest cut uses the current
 * portfolio sheet; earlier cuts use the history sheet joined by id or name.
 */
export const buildSnapshots = (ds: Dataset, cuts: number[], th: Thresholds): Map<number, SnapItem[]> => {
    const snapshots = new Map<number, SnapItem[]>()
    const latest = cuts[cuts.length - 1]
    const current = new Map(ds.portfolio.map(item => [item.key, item]))
    const byCut = new Map<number, HistoryRow[]>()

    for (const row of ds.history) {
        const list = byCut.get(row.cut)

        if (list) list.push(row)
        else byCut.set(row.cut, [row])
    }

    for (const cut of cuts) {
        if (cut === latest) {
            snapshots.set(cut, ds.portfolio.map(item => makeItem(item, {
                progress: item.progress,
                planned: item.planned,
                status: item.status ?? statusFromProgress(item.progress),
                end: item.end,
                start: item.start
            }, cut, item.health, th)))

            continue
        }

        const rows = byCut.get(cut) ?? []
        const seen = new Set<string>()
        const items: SnapItem[] = []

        for (const row of rows) {
            if (seen.has(row.key)) continue

            seen.add(row.key)

            const base = current.get(row.key)
            const merged: Initiative | HistoryRow = base
                ? base
                : row

            items.push(makeItem(merged, {
                progress: row.progress,
                planned: row.planned,
                status: row.status ?? statusFromProgress(row.progress),
                end: row.end ?? base?.end ?? null,
                start: base?.start ?? null
            }, cut, null, th))
        }

        snapshots.set(cut, items)
    }

    return snapshots
}

/* ------------------------------------------------------------------ */
/* Filtering                                                           */
/* ------------------------------------------------------------------ */

export interface Matcher {
    dims: (record: Partial<Dims>, sheet: SheetKey) => boolean
    range: (start: number | null, end: number | null, sheet: SheetKey, fields: string[]) => boolean
    period: (period: Period | null, sheet: SheetKey, field: string) => boolean
}

const hasField = (ds: Dataset, sheet: SheetKey, field: string): boolean => {
    const fields = ds.fields[sheet] ?? []

    if (fields.includes(field)) return true

    // Risk level can come from likelihood times impact
    if (sheet === 'risks' && field === 'criticality') return fields.includes('score') || (fields.includes('probability') && fields.includes('impact'))

    return false
}

export const createMatcher = (ds: Dataset, filters: Filters): Matcher => {
    const wanted = {
        division: filters.division ? normalizeValue(filters.division) : null,
        bu: filters.bu ? normalizeValue(filters.bu) : null,
        owner: filters.owner ? normalizeValue(filters.owner) : null,
        program: filters.program ? normalizeValue(filters.program) : null,
        domain: filters.domain ? normalizeValue(filters.domain) : null
    }

    const range = periodRange(filters.period)
    const textFields = ['division', 'bu', 'owner', 'program', 'domain'] as const

    return {
        dims: (record, sheet) => {
            for (const field of textFields) {
                const value = wanted[field]

                if (value && hasField(ds, sheet, field) && normalizeValue(record[field] ?? '') !== value) return false
            }

            if (hasField(ds, sheet, 'criticality')) {
                if (filters.criticality && record.criticality !== filters.criticality) return false
                if (filters.onlyCritical && record.criticality !== 'critical') return false
            }

            return true
        },
        range: (start, end, sheet, fields) => {
            if (!range) return true
            if (!fields.some(field => hasField(ds, sheet, field))) return true

            const from = start ?? end
            const to = end ?? start

            if (from === null || to === null) return false

            return from <= range.end && to >= range.start
        },
        period: (period, sheet, field) => {
            if (!range) return true
            if (!hasField(ds, sheet, field)) return true
            if (!period || period.kind === 'text') return false

            return period.start <= range.end && period.end >= range.start
        }
    }
}

/* ------------------------------------------------------------------ */
/* Filter options                                                      */
/* ------------------------------------------------------------------ */

export interface Option {
    value: string
    label: string
    count: number
}

export interface PeriodOption {
    key: string
    kind: 'year' | 'quarter'
    year: number
    quarter: number | null
}

export interface FilterOptions {
    divisions: Option[]
    bus: (Option & { divisions: string[] })[]
    owners: Option[]
    programs: Option[]
    domains: Option[]
    criticalities: Criticality[]
    periods: PeriodOption[]
}

const collect = (values: string[]): Option[] => {
    const map = new Map<string, { labels: Map<string, number>, count: number }>()

    for (const value of values) {
        const key = normalizeValue(value)

        if (!key) continue

        const entry = map.get(key) ?? { labels: new Map(), count: 0 }

        entry.count++
        entry.labels.set(value, (entry.labels.get(value) ?? 0) + 1)
        map.set(key, entry)
    }

    return [...map.values()]
        .map(entry => {
            const label = [...entry.labels.entries()].sort((a, b) => b[1] - a[1])[0][0]

            return { value: label, label, count: entry.count }
        })
        .sort((a, b) => a.label.localeCompare(b.label, 'es'))
}

const allDims = (ds: Dataset): Partial<Dims>[] => [
    ...ds.portfolio,
    ...ds.compliance,
    ...ds.design,
    ...ds.findings,
    ...ds.tiers,
    ...ds.risks,
    ...ds.budget,
    ...ds.agreements,
    ...ds.csf,
    ...ds.basics,
    ...ds.volumetry
]

export const filterOptions = (ds: Dataset): FilterOptions => {
    const dims = allDims(ds)
    const bus = collect(dims.map(item => item.bu ?? ''))
    const divisionsByBu = new Map<string, Set<string>>()

    for (const item of dims) {
        if (!item.bu || !item.division) continue

        const key = normalizeValue(item.bu)
        const set = divisionsByBu.get(key) ?? new Set()

        set.add(normalizeValue(item.division))
        divisionsByBu.set(key, set)
    }

    const criticalities = new Set<Criticality>()

    for (const item of dims) {
        if (item.criticality) criticalities.add(item.criticality)
    }

    const dates: number[] = []
    const push = (value: number | null | undefined) => {
        if (typeof value === 'number') dates.push(value)
    }

    ds.portfolio.forEach(item => {
        push(item.start)
        push(item.end)
    })
    ds.compliance.forEach(item => push(item.due))
    ds.design.forEach(item => {
        push(item.start)
        push(item.end)
    })
    ds.findings.forEach(item => push(item.detected))
    ds.agreements.forEach(item => push(item.due))
    ds.volumetry.forEach(item => {
        if (item.period.kind !== 'text') push(item.period.start)
    })
    ds.budget.forEach(item => {
        if (item.period && item.period.kind !== 'text') push(item.period.start)
    })

    const quarters = new Map<string, PeriodOption>()
    const years = new Map<number, PeriodOption>()

    for (const ts of dates) {
        const { year, quarter } = quarterOf(ts)

        years.set(year, { key: yearKey(year), kind: 'year', year, quarter: null })
        quarters.set(quarterKey(year, quarter), { key: quarterKey(year, quarter), kind: 'quarter', year, quarter })
    }

    const periods: PeriodOption[] = []

    for (const year of [...years.keys()].sort((a, b) => b - a)) {
        periods.push(years.get(year) as PeriodOption)

        const inYear = [...quarters.values()].filter(option => option.year === year).sort((a, b) => (b.quarter ?? 0) - (a.quarter ?? 0))

        periods.push(...inYear)
    }

    const order: Criticality[] = ['critical', 'high', 'medium', 'low']

    return {
        divisions: collect(dims.map(item => item.division ?? '')),
        bus: bus.map(option => ({ ...option, divisions: [...(divisionsByBu.get(normalizeValue(option.value)) ?? [])] })),
        owners: collect(dims.map(item => item.owner ?? '')),
        programs: collect(dims.map(item => item.program ?? '')),
        domains: collect(dims.map(item => item.domain ?? '')),
        criticalities: order.filter(level => criticalities.has(level)),
        periods
    }
}

/* ------------------------------------------------------------------ */
/* State "as of" a date for dated registers                            */
/* ------------------------------------------------------------------ */

export const findingOpenAt = (item: Finding, at: number): boolean => {
    if (item.detected !== null && item.detected > at) return false
    if (item.closed !== null) return item.closed > at

    return item.status !== 'closed' && item.status !== 'accepted'
}

export const agreementOpenAt = (item: Agreement, at: number): boolean => {
    if (item.date !== null && item.date > at) return false
    if (item.closed !== null) return item.closed > at

    return item.status !== 'done' && item.status !== 'cancelled'
}

export const complianceDoneAt = (item: ComplianceItem, at: number): boolean => {
    if (item.closed !== null) return item.closed <= at

    return item.status === 'compliant'
}

export const complianceOverdueAt = (item: ComplianceItem, at: number): boolean => {
    if (item.status === 'notApplicable') return false

    return item.due !== null && item.due < at && !complianceDoneAt(item, at)
}

export const designOverdueAt = (item: DesignActivity, at: number): boolean => {
    return item.end !== null && item.end < at && item.status !== 'closed' && item.status !== 'cancelled'
}

/* ------------------------------------------------------------------ */
/* Budget                                                              */
/* ------------------------------------------------------------------ */

export interface BudgetEdit {
    plan?: number | null
    actual?: number | null
}

export const applyBudgetEdits = (rows: BudgetRow[], edits: Record<number, BudgetEdit>): BudgetRow[] => {
    if (!Object.keys(edits).length) return rows

    return rows.map(row => {
        const edit = edits[row.row]

        if (!edit) return row

        return {
            ...row,
            plan: edit.plan !== undefined ? edit.plan : row.plan,
            actual: edit.actual !== undefined ? edit.actual : row.actual
        }
    })
}

export interface CurrencyContext {
    display: string
    rate: number | null
    canSwitch: boolean
    convert: (amount: number | null, from: string) => number | null
}

/** Display currency and conversion with the exchange rate from the file. */
export const currencyContext = (ds: Dataset, requested: string | null): CurrencyContext => {
    const counts = new Map<string, number>()

    for (const row of ds.budget) counts.set(row.currency, (counts.get(row.currency) ?? 0) + 1)

    const base = ds.params.baseCurrency ?? ([...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'MXN')
    const rate = ds.params.exchangeRate
    const canSwitch = rate !== null && rate > 0
    const display = canSwitch && (requested === 'MXN' || requested === 'USD') ? requested : base

    const convert = (amount: number | null, from: string): number | null => {
        if (amount === null) return null
        if (from === display) return amount
        if (!rate) return null
        if (from === 'MXN' && display === 'USD') return amount / rate
        if (from === 'USD' && display === 'MXN') return amount * rate

        return null
    }

    return { display, rate, canSwitch, convert }
}

/* ------------------------------------------------------------------ */
/* Small statistics helpers                                            */
/* ------------------------------------------------------------------ */

export const mean = (values: (number | null | undefined)[]): number | null => {
    const list = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))

    if (!list.length) return null

    return list.reduce((sum, value) => sum + value, 0) / list.length
}

export const sum = (values: (number | null | undefined)[]): number => {
    return values.reduce<number>((total, value) => total + (typeof value === 'number' && Number.isFinite(value) ? value : 0), 0)
}

export const countBy = <T, K extends string>(items: T[], key: (item: T) => K | null | undefined): Map<K, number> => {
    const map = new Map<K, number>()

    for (const item of items) {
        const value = key(item)

        if (value === null || value === undefined) continue

        map.set(value, (map.get(value) ?? 0) + 1)
    }

    return map
}

export const groupBy = <T>(items: T[], key: (item: T) => string): Map<string, T[]> => {
    const map = new Map<string, T[]>()

    for (const item of items) {
        const value = key(item)
        const list = map.get(value)

        if (list) list.push(item)
        else map.set(value, [item])
    }

    return map
}

export const delta = (current: number | null, previous: number | null): number | null => {
    if (current === null || previous === null) return null

    return current - previous
}
